import { ReactNode } from 'react'

type ChipColor = 'default' | 'primary' | 'success' | 'warning' | 'danger'
type ChipVariant = 'soft' | 'solid'
type ChipSize = 'sm' | 'md'

interface ChipProps {
    children: ReactNode
    onRemove?: () => void
    variant?: ChipVariant
    color?: ChipColor
    size?: ChipSize
    icon?: ReactNode
    className?: string
}

const colorClasses: Record<ChipVariant, Record<ChipColor, string>> = {
    soft: {
        default: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600',
        primary: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700',
        success: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700',
        warning: 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-700',
        danger: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700',
    },
    solid: {
        default: 'bg-slate-600 text-white border-transparent',
        primary: 'bg-blue-600 text-white border-transparent',
        success: 'bg-green-600 text-white border-transparent',
        warning: 'bg-yellow-500 text-white border-transparent',
        danger: 'bg-red-600 text-white border-transparent',
    },
}

const sizeClasses: Record<ChipSize, string> = {
    sm: 'px-2 py-0.5 text-xs gap-1',
    md: 'px-2.5 py-1 text-sm gap-1.5',
}

export const Chip = ({ children, onRemove, variant = 'soft', color = 'default', size = 'md', icon, className = '' }: ChipProps) => {
    return (
        <span className={`inline-flex items-center rounded-full border font-medium ${colorClasses[variant][color]} ${sizeClasses[size]} ${className}`}>
            {icon && <span className="shrink-0">{icon}</span>}
            {children}
            {onRemove && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation()
                        onRemove()
                    }}
                    className="ml-0.5 shrink-0 rounded-full p-0.5 transition-colors hover:bg-black/10 dark:hover:bg-white/10"
                    aria-label="Remove"
                >
                    <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            )}
        </span>
    )
}
